<script setup lang="ts">
  import { capitalize } from '@/helpers/converter'
  import { classList, ICharacterCdn, IClassJobCdn, useCJStore } from '@/store/ClassesJobs'
  import StatsSkillsAbilities from '@/views/game/ClassesJobs/StatsSkillsAbilities.vue'
  import { computed, onMounted, ref, watch, watchEffect } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useDisplay } from 'vuetify'
  const { mobile } = useDisplay()
  const cjStore = useCJStore()
  const router = useRouter()
  const route = useRoute()

  const selectedClass = ref((route.params.className as string).toLowerCase())
  const classesJobsList = computed<IClassJobCdn[]>((): IClassJobCdn[] => cjStore.classesJobsList)
  const filteredJobList = computed<ICharacterCdn[]>(
    (): ICharacterCdn[] =>
      classesJobsList.value.find(
        (item: IClassJobCdn) => item.name.toLowerCase() === (route.params.className as string).toLowerCase()
      )?.Children || []
  )
  const characterData = ref<ICharacterCdn>()
  const $video = ref()

  onMounted(async () => {
    classNameGuard()
    window.scrollTo(0, 0)
    await cjStore.fetch()
    setCharacter()
  })

  const classNameGuard = () => {
    if (!classList.find((item) => item === selectedClass.value)) goBackToList()
  }
  const characterGuard = () => {
    if (!characterData.value)
      goToCharacter(filteredJobList.value[0].id.toString(), route.params.className as string, true)
  }

  const goBackToList = () => {
    router.replace({
      name: 'classes-and-jobs',
    })
  }
  const goToCharacter = (classID: string, className: string, replace = false) => {
    const navType = replace ? 'replace' : 'push'
    router[navType]({
      name: 'classes-and-jobs-detail',
      params: {
        classID,
        className,
      },
    })
  }

  const setCharacter = () => {
    selectedClass.value = (route.params.className as string).toLowerCase()
    characterData.value = filteredJobList.value.find(
      (item: ICharacterCdn) => item.id.toString() === route.params.classID
    )
    characterGuard()
  }

  const selectCharacter = (classID: number) => {
    if (classID === characterData.value?.id) return
    goToCharacter(classID.toString(), route.params.className as string)
  }
  const selectClass = (className: string) => {
    if (selectedClass.value === className) return
    goToCharacter(route.params.classID.toString(), className)
  }
  watch(
    () => route.params,
    ({ className, classID }) => {
      if (classID && className) setCharacter()
    }
  )

  watchEffect(() => {
    if ($video.value) {
      $video.value.playbackRate = 1.3
    }
  })
</script>
<template>
  <div class="classes-jobs-detail">
    <div class="cjd-bg" :class="`${selectedClass}`"></div>
    <div class="content-wrapper">
      <div
        v-ga-section-view
        class="content"
        :class="`${selectedClass}`"
        data-section-name="Skills&Abilities"
        data-section-number="1"
      >
        <div>
          <div>
            <div v-if="!mobile" class="cjd-title">
              {{ characterData?.name }}
              <div class="class-emblem" :class="`${selectedClass}`"></div>
              <div class="class-tag" :class="`${selectedClass}`">{{ capitalize(selectedClass) }} Class</div>
            </div>
            <div v-if="!mobile" class="description">
              {{ characterData?.description }}
            </div>
          </div>
          <div class="container">
            <div>
              <div v-if="!mobile" class="character">
                <img :alt="characterData?.name" :src="characterData?.characterImage" />
              </div>
              <div class="preview">
                <video :key="characterData?.characterVideo" ref="$video" autoplay loop muted playsinline>
                  <source :src="characterData?.characterVideo" type="video/mp4" />
                </video>
              </div>
            </div>
            <StatsSkillsAbilities v-if="characterData" :character-data="characterData" />
          </div>
        </div>
      </div>
      <ClassJobSelection
        v-ga-section-view
        :class-id="characterData?.id"
        :class-name="selectedClass"
        data-section-name="Character Filter"
        data-section-number="2"
        :track-arrows="true"
        @change:class-id="selectCharacter"
        @change:class-name="selectClass"
      >
        <template #mobile-content="{ character }">
          <div class="mobile-description">{{ character.description }}</div>
          <div class="mobile-character">
            <img :alt="character.name" :src="character.characterImage" />
          </div>
        </template>
      </ClassJobSelection>
    </div>
    <PageBanners />
  </div>
</template>
<style lang="scss" scoped>
  $imgPath: '@/assets/imgs/classesJobs';
  $jobs: (
    warrior: var(--job-warriorColor),
    magician: var(--job-magicianColor),
    bowman: var(--job-bowmanColor),
    thief: var(--job-thiefColor),
    pirate: var(--job-pirateColor),
  );

  .classes-jobs-detail {
    background-color: #f7f7f7;
    user-select: none;
    margin-bottom: 1.5rem;

    .cjd-bg {
      position: absolute;
      height: pxToRem(637.5px);
      width: 100%;
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
      @each $key, $val in $jobs {
        &.#{$key} {
          background-image: url(#{$imgPath}/bgs/#{$key}.png);
        }
      }
    }

    .content {
      height: pxToRem(637.5px);
      padding: pxToRem(30.25px) pxToRem(166.325px) pxToRem(20.15px);
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      > div {
        max-width: 1600px;
        margin: 0 auto;
        width: 100%;
      }
    }
    .cjd-title {
      font-size: pxToRem(48.375px);
      font-family: Glory;
      font-weight: bold;
      line-height: pxToRem(54.425px);
      color: #fff;
      text-transform: uppercase;
      display: flex;
      align-items: center;
      margin: 0;
    }
    .class-emblem {
      height: pxToRem(54.425px);
      width: pxToRem(54.425px);
      background-repeat: no-repeat;
      background-size: contain;
      margin: 0 pxToRem(12.5px);
      @each $key, $val in $jobs {
        &.#{$key} {
          background-image: url(#{$imgPath}/emblems/full/#{$key}.png);
        }
      }
    }
    .class-tag {
      background-repeat: no-repeat;
      background-size: contain;
      line-height: pxToRem(32.25px);
      font-size: pxToRem(18.15px);
      font-weight: normal;
      text-transform: none;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: Glegoo;
      &.warrior {
        height: pxToRem(32.25px);
        width: pxToRem(163.3px);
        background-image: url(#{$imgPath}/emblems/tags/warrior.png);
      }
      &.bowman {
        height: pxToRem(33.25px);
        width: pxToRem(173.375px);
        background-image: url(#{$imgPath}/emblems/tags/bowman.png);
      }
      &.magician {
        height: pxToRem(33.25px);
        width: pxToRem(177.425px);
        background-image: url(#{$imgPath}/emblems/tags/magician.png);
      }
      &.pirate {
        height: pxToRem(33.25px);
        width: pxToRem(149.2px);
        background-image: url(#{$imgPath}/emblems/tags/pirate.png);
      }
      &.thief {
        height: pxToRem(33.25px);
        width: pxToRem(140.125px);
        background-image: url(#{$imgPath}/emblems/tags/thief.png);
      }
    }
    .description {
      font-size: pxToRem(18.15px);
      font-family: Glegoo;
      color: #fff;
      line-height: pxToRem(32.25px);
      margin: pxToRem(12.5px) 0;
      height: pxToRem(64.525px);
    }
    .container {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      > div:first-of-type {
        display: flex;
        align-items: flex-end;
      }
    }
    .character {
      height: pxToRem(425px);
      width: pxToRem(375px);
      display: flex;
      img {
        margin: auto auto;
        max-height: 100%;
        max-width: 100%;
      }
    }
    .preview {
      height: pxToRem(403.225px);
      width: pxToRem(715.725px);
      border: pxToRem(2.075px) solid #fff;
      border-radius: pxToRem(6.25px);
      overflow: hidden;
      margin-left: pxToRem(6.25px);
      background-color: rgba(255, 255, 255, 0.75);
      position: relative;
      &:after {
        content: '';
        position: absolute;
        transition: opacity 0.3s;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        background: url('@/assets/imgs/common/loading.gif') white no-repeat center center / 100px 100px;
      }
      video {
        height: 100%;
        width: 100%;
        object-fit: cover;
        position: relative;
        z-index: 1;
      }
    }

    :deep(.page-banners) {
      margin-top: 40px;
    }
  }

  @media #{$tablet} {
    .classes-jobs-detail {
      background-color: #fff;
      .cjd-bg {
        height: pxToRemTablet(1045.988px);
        transform: translateY(-1px);
        margin-top: pxToRemTablet(148.01px);
        @each $key, $val in $jobs {
          &.#{$key} {
            background-image: url(#{$imgPath}/bgs/tablet/#{$key}.png);
          }
        }
      }
      .content {
        height: auto;
        padding: 0;
      }
      .content-wrapper {
        display: flex;
        flex-direction: column-reverse;
      }
      .cjd-title {
        margin-top: pxToRemTablet(19px);
        justify-content: center;
        font-size: pxToRemTablet(47.994px);
      }
      .class-emblem {
        margin: 0 pxToRemTablet(9.5px);
        height: pxToRemTablet(50.008px);
        width: pxToRemTablet(50.008px);
      }
      .class-tag {
        font-size: pxToRemTablet(18.012px);
        &.warrior {
          height: pxToRemTablet(31.616px);
          width: pxToRemTablet(161.386px);
        }
        &.bowman {
          height: pxToRemTablet(31.616px);
          width: pxToRemTablet(171.38px);
        }
        &.magician {
          height: pxToRemTablet(31.616px);
          width: pxToRemTablet(175.408px);
        }
        &.pirate {
          height: pxToRemTablet(31.616px);
          width: pxToRemTablet(147.402px);
        }
        &.thief {
          height: pxToRemTablet(31.616px);
          width: pxToRemTablet(138.396px);
        }
      }
      .description {
        font-size: pxToRemTablet(18.012px);
        line-height: 2;
        width: 90%;
        margin: 0 auto pxToRemTablet(19px);
        height: auto;
      }
      .container {
        flex-direction: column;
        align-items: center;
      }
      .character {
        height: pxToRemTablet(350.018px);
        width: pxToRemTablet(325.014px);
      }
      .preview {
        height: pxToRemTablet(281.998px);
        width: pxToRemTablet(486.78px);
        border: pxToRemTablet(9.12px) solid #dfdfdf;
        border-radius: pxToRemTablet(1.9px);
        margin-bottom: pxToRemTablet(38px);
        position: relative;
        overflow: visible;
        &:before {
          content: '';
          border-right: pxToRemTablet(24.7px) solid #dfdfdf;
          border-left: pxToRemTablet(24.7px) solid transparent;
          border-top: pxToRemTablet(19px) solid transparent;
          border-bottom: pxToRemTablet(19px) solid transparent;
          position: absolute;
          left: pxToRemTablet(-53.2px);
          top: 50%;
          transform: translateY(-50%);
        }
      }
      :deep(.page-banners) {
        margin-top: 15px;
      }
    }
  }

  @media #{$mobile} {
    .classes-jobs-detail {
      .cjd-bg {
        margin: 0;
        height: pxToRemMobile(1121.004px);
        @each $key, $val in $jobs {
          &.#{$key} {
            background-image: url(#{$imgPath}/bgs/mobile/#{$key}.png);
          }
        }
      }
      .content-wrapper {
        padding-top: pxToRemMobile(30.006px);
      }
      .mobile-description {
        font-size: pxToRemMobile(18px);
        color: #fff;
        font-family: 'Glegoo';
        font-weight: normal;
        max-width: pxToRemMobile(477px);
        text-align: center;
        line-height: 1.75;
      }
      .mobile-character {
        height: pxToRemMobile(486px);
        width: pxToRemMobile(450px);
        display: flex;
        img {
          margin: auto auto;
          max-height: 100%;
          max-width: 100%;
        }
      }
      .preview {
        margin-top: pxToRemMobile(18px);
        width: pxToRemMobile(550.008px);
        height: pxToRemMobile(248.004px);
        border: pxToRemMobile(9px) solid #fff;
        border-radius: pxToRemMobile(4.5px);
        &:before {
          border-right: pxToRemMobile(22.5px) solid transparent;
          border-left: pxToRemMobile(22.5px) solid transparent;
          border-top: pxToRemMobile(27px) solid transparent;
          border-bottom: pxToRemMobile(27px) solid #fff;
          top: pxToRemMobile(-54px);
          left: 50%;
          transform: translateX(-50%);
        }
      }
    }
  }
</style>
